import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createRootRoute, createRoute, createRouter, RouterProvider } from '@tanstack/react-router'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { AuthProvider } from './contexts/AuthContext'
import './index.css'

import { DashboardLayout } from './components/DashboardLayout'
import { Campaigns } from './pages/Campaigns'
import { Dashboard } from './pages/Dashboard'
// Pages
import { Login } from './pages/Login'
import { Payments } from './pages/Payments'
import { Profile } from './pages/Profile'
import { Signup } from './pages/Signup'
import { Statistics } from './pages/Statistics'

// Create a root route
const rootRoute = createRootRoute()

// Create routes
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: Login,
})

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: Signup,
})

// Protected routes with layout
const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'layout',
  component: DashboardLayout,
})

const dashboardRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/dashboard',
  component: Dashboard,
})

const campaignsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/campaigns',
  component: Campaigns,
})

const paymentsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/payments',
  component: Payments,
})

const statisticsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/statistics',
  component: Statistics,
})

const profileRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/profile',
  component: Profile,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: ({ navigate }) => {
    navigate({ to: '/dashboard' })
  },
})

// Create the route tree
const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  layoutRoute.addChildren([dashboardRoute, campaignsRoute, paymentsRoute, statisticsRoute, profileRoute]),
])

// Create the router
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
})

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      refetchOnWindowFocus: false,
    },
  },
})

// Declare module for TypeScript
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const rootElement = document.getElementById('root')

if (!rootElement) {
  console.error('Root element not found!')
  document.body.innerHTML = '<h1>Error: Root element not found</h1>'
} else {
  try {
    ReactDOM.createRoot(rootElement).render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <RouterProvider router={router} />
          </AuthProvider>
        </QueryClientProvider>
      </React.StrictMode>,
    )
    console.log('App mounted successfully')
  } catch (error) {
    console.error('Error mounting app:', error)
    document.body.innerHTML = `<h1>Error loading app</h1><pre>${error}</pre>`
  }
}
